import React, { useState, useRef } from 'react';

function MobileLocationField({
  address,
  onAddressChange,
  onAddressSelect,
  MAPBOX_TOKEN,
}) {
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const suggestionTimeoutRef = useRef(null);

  const fetchAddressSuggestions = async (query) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      setIsLoadingSuggestions(false);
      return;
    }

    setIsLoadingSuggestions(true);

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          query
        )}.json?country=GB&access_token=${MAPBOX_TOKEN}&limit=5`
      );
      const data = await response.json();

      if (data.features) {
        setAddressSuggestions(data.features);
      }
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      setAddressSuggestions([]);
    }

    setIsLoadingSuggestions(false);
  };

  const handleAddressChange = (value) => {
    onAddressChange(value);

    // Clear existing timeout
    if (suggestionTimeoutRef.current) {
      clearTimeout(suggestionTimeoutRef.current);
    }

    // Set new timeout to fetch suggestions
    suggestionTimeoutRef.current = setTimeout(() => {
      fetchAddressSuggestions(value);
    }, 300);

    setShowSuggestions(true);
  };

  const handleSuggestionClick = (suggestion) => {
    const selectedAddress = suggestion.place_name;
    const coordinates = {
      lat: suggestion.center[1],
      lng: suggestion.center[0],
    };

    onAddressSelect(selectedAddress, coordinates);
    setShowSuggestions(false);
    setAddressSuggestions([]);
  };

  const handleInputFocus = () => {
    if (addressSuggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => setShowSuggestions(false), 200);
  };

  return (
    <div>
      <div className="px-4">
        <label
          className="block font-medium text-black text-sm mb-1"
          htmlFor="mobile-location"
        >
          Location
        </label>

        <div className="relative">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
            focusable="false"
            className="absolute left-3 top-1/2 -translate-y-1/2"
          >
            <path
              d="M15.8401 15.0328L12.1014 11.294V11.2948C13.6412 9.47462 14.1262 6.98617 13.3823 4.72177C12.6384 2.45665 10.7726 0.739824 8.45402 0.187145C6.13541 -0.365588 3.69556 0.324796 2.01014 2.01013C0.324816 3.69546 -0.365619 6.1354 0.187167 8.45402C0.739899 10.7726 2.45667 12.6385 4.72178 13.3823C6.98617 14.1262 9.47458 13.6412 11.2947 12.1014L15.0335 15.8401H15.0328C15.2567 16.0562 15.6126 16.0527 15.833 15.833C16.0527 15.6126 16.0562 15.2568 15.8401 15.0328ZM1.17216 6.87791C1.17216 5.36446 1.77341 3.91313 2.84321 2.8432C3.91302 1.77326 5.36449 1.17214 6.87792 1.17214C8.39134 1.17214 9.84269 1.77339 10.9126 2.8432C11.9826 3.913 12.5837 5.36448 12.5837 6.87791C12.5837 8.39134 11.9824 9.84269 10.9126 10.9126C9.84281 11.9826 8.39134 12.5837 6.87792 12.5837C5.36518 12.5823 3.91515 11.9803 2.8454 10.9105C1.77558 9.84065 1.1736 8.39061 1.17216 6.87791Z"
              fill="#6B7280"
            />
          </svg>

          <input
            type="text"
            name="s"
            id="mobile-location"
            className="pr-3 pl-12 min-h-[42px] w-full placeholder:text-[#6B7280] placeholder:text-sm placeholder:font-light text-sm bg-grey-100 border border-[#ebebeb] rounded"
            aria-label="Enter postcode or location"
            placeholder="Enter postcode or location"
            value={address}
            onChange={(e) => handleAddressChange(e.target.value)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            autoComplete="off"
          />

          {/* Loading Indicator */}
          {isLoadingSuggestions && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <svg
                className="animate-spin h-4 w-4 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          )}

          {/* Suggestions Dropdown */}
          {showSuggestions && addressSuggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#ebebeb] rounded shadow-lg z-50 max-h-48 overflow-y-auto">
              {addressSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion.place_name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MobileLocationField;
